import { useState } from "react";
import { useGameStore } from "../../store/gameStore";
import { useApi } from "../../hooks/useApi";
import { hapticFeedback, hapticNotification } from "../../telegram/init";

interface IslandData {
  id: number;
  forestLevel: number;
  mineLevel: number;
  forestDonated: number;
  mineDonated: number;
  forestNextLevel: number;
  mineNextLevel: number;
}

interface Props {
  island: IslandData;
  type: "FOREST" | "MINE";
  onClose: () => void;
  onSuccess: () => void;
}

const PRESETS = [100, 500, 1000];

export function DonationPanel({ island, type, onClose, onSuccess }: Props) {
  const cities       = useGameStore((s) => s.cities);
  const activeCityId = useGameStore((s) => s.activeCityId);
  const updateCity   = useGameStore((s) => s.updateCity);
  const api          = useApi();

  const city = cities.find((c) => c.id === activeCityId);

  const currentLevel  = type === "FOREST" ? island.forestLevel  : island.mineLevel;
  const donated       = type === "FOREST" ? island.forestDonated : island.mineDonated;
  const nextThreshold = type === "FOREST" ? island.forestNextLevel : island.mineNextLevel;
  const progress      = nextThreshold > 0 ? Math.min(donated / nextThreshold, 1) : 0;

  const woodBalance  = Math.floor(city?.wood ?? 0);
  const maxDonate    = Math.min(woodBalance, nextThreshold - donated);

  const [amount, setAmount] = useState<number>(Math.min(100, maxDonate));
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const icon  = type === "FOREST" ? "\u{1F332}" : "\u26CF\uFE0F";
  const label = type === "FOREST" ? "Sawmill" : "Mine";
  const luxuryIcon = type === "MINE" ? "\u2728" : "";

  async function handleDonate() {
    if (!activeCityId || !city || amount <= 0) return;
    setLoading(true);
    setError(null);
    try {
      await api.donateToIsland(island.id, activeCityId, type, amount);
      hapticNotification("success");
      // Refresh city so wood balance updates
      const cityData = await api.getCity(activeCityId);
      updateCity({ id: activeCityId, ...cityData });
      onSuccess();
    } catch (e: any) {
      setError(e.message);
      hapticNotification("error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="panel-overlay" onClick={onClose} />
      <div className="panel-sheet">
        <div className="panel-handle" />

        {/* Header */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "var(--space-sm)", marginBottom: "var(--space-lg)" }}>
          <span style={{ fontSize: "2rem" }}>{icon}{luxuryIcon}</span>
          <h2 style={{ color: "var(--terra-dark)" }}>Donate to {label}</h2>
          <div style={{
            fontFamily: "var(--font-display)",
            fontSize: "0.65rem",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: "var(--ink-faded)",
          }}>
            Level {currentLevel} — Shared with all islanders
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ marginBottom: "var(--space-lg)" }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "var(--space-xs)",
            fontSize: "0.75rem",
            color: "var(--ink-faded)",
            fontFamily: "var(--font-display)",
          }}>
            <span>{"\u{1FAB5}"} {Math.floor(donated).toLocaleString()} donated</span>
            <span>Next lv: {nextThreshold.toLocaleString()}</span>
          </div>
          <div style={{
            height: "8px",
            borderRadius: "4px",
            background: "var(--parchment)",
            border: "1px solid var(--sand)",
            overflow: "hidden",
          }}>
            <div style={{
              height: "100%",
              width: `${progress * 100}%`,
              borderRadius: "4px",
              background: type === "FOREST"
                ? "linear-gradient(90deg, var(--olive), var(--olive-light))"
                : "linear-gradient(90deg, var(--aegean-deep), var(--aegean))",
              transition: "width 0.3s ease",
            }} />
          </div>
        </div>

        {/* Wood balance */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--space-sm)",
          padding: "var(--space-md)",
          background: "var(--parchment)",
          borderRadius: "var(--radius)",
          border: "1px solid var(--sand)",
          marginBottom: "var(--space-lg)",
        }}>
          <span style={{ fontSize: "1.2rem" }}>{"\u{1FAB5}"}</span>
          <div>
            <div style={{
              fontFamily: "var(--font-display)",
              fontSize: "0.62rem",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "var(--ink-faded)",
            }}>
              Your Wood
            </div>
            <div style={{
              fontSize: "1rem",
              fontWeight: 700,
              color: woodBalance > 0 ? "var(--olive)" : "var(--terra)",
            }}>
              {woodBalance.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Preset amounts */}
        <div style={{
          fontFamily: "var(--font-display)",
          fontSize: "0.62rem",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "var(--ink-faded)",
          marginBottom: "var(--space-sm)",
        }}>
          Donation Amount
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "var(--space-sm)",
          marginBottom: "var(--space-lg)",
        }}>
          {PRESETS.map((preset) => {
            const canAfford = preset <= woodBalance && preset <= maxDonate;
            return (
              <button
                key={preset}
                disabled={!canAfford}
                onClick={() => { setAmount(preset); hapticFeedback("light"); }}
                style={{
                  padding: "8px 0",
                  borderRadius: "var(--radius)",
                  border: amount === preset
                    ? "2px solid var(--terra)"
                    : "1.5px solid var(--sand)",
                  background: amount === preset
                    ? "linear-gradient(135deg, var(--terra), var(--terra-dark))"
                    : "var(--parchment)",
                  color: amount === preset ? "var(--papyrus-light)" : "var(--ink)",
                  fontFamily: "var(--font-display)",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  opacity: canAfford ? 1 : 0.35,
                }}
              >
                {preset.toLocaleString()}
              </button>
            );
          })}
          {/* Max button */}
          <button
            disabled={maxDonate <= 0}
            onClick={() => { setAmount(Math.max(1, maxDonate)); hapticFeedback("light"); }}
            style={{
              padding: "8px 0",
              borderRadius: "var(--radius)",
              border: amount === maxDonate && maxDonate > 0
                ? "2px solid var(--terra)"
                : "1.5px solid var(--sand)",
              background: amount === maxDonate && maxDonate > 0
                ? "linear-gradient(135deg, var(--terra), var(--terra-dark))"
                : "var(--parchment)",
              color: amount === maxDonate && maxDonate > 0 ? "var(--papyrus-light)" : "var(--ink)",
              fontFamily: "var(--font-display)",
              fontSize: "0.75rem",
              fontWeight: 600,
              opacity: maxDonate > 0 ? 1 : 0.35,
            }}
          >
            Max
          </button>
        </div>

        {/* Donate button */}
        <button
          className="btn-primary"
          disabled={loading || amount <= 0 || amount > woodBalance}
          onClick={handleDonate}
          style={{ width: "100%", padding: "14px" }}
        >
          {loading ? "Donating…" : `Donate ${amount.toLocaleString()} Wood`}
        </button>

        {error && (
          <div style={{
            marginTop: "var(--space-md)",
            padding: "var(--space-md)",
            background: "rgba(196, 91, 58, 0.1)",
            border: "1px solid rgba(196, 91, 58, 0.3)",
            borderRadius: "var(--radius)",
            color: "var(--terra-dark)",
            fontSize: "0.85rem",
            textAlign: "center",
          }}>
            {error}
          </div>
        )}
      </div>
    </>
  );
}
